<template>
    <section ref="priceCalculator" class="section price-calculator">
        <div class="wrap">
            <div
                v-lazy-load-image="require('~/assets/images/price-calculator-bg.webp')"
                class="price-calculator__bg-image"
            />

            <div class="price-calculator__card">
                <template v-if="leadPriceCalculations">
                    <i18n
                        class="price-calculator__result-heading"
                        tag="h2"
                        path="sections.price_calculator.result_heading"
                    >
                        <template #price>
                            <span class="price-calculator__result-price">{{
                                formattedPrice(
                                    leadPriceCalculations.priceHighMedian * 7,
                                    leadPriceCalculations.currency,
                                    0
                                )
                            }}</span>
                        </template>
                        <template #vehicleType>
                            <span>{{ vehicleTypeText() }}</span>
                        </template>
                    </i18n>

                    <div id="control-earnings">
                        <Notice>
                            <template #heading>
                                {{ $t('sections.price_calculator.result_notice_title') }}
                            </template>
                            <template #default>
                                <p>{{ resultNoticeDescription }}</p>
                                <p>{{ $t('sections.price_calculator.result_notice_desc2') }}</p>
                            </template>
                        </Notice>
                    </div>

                    <div class="price-calculator__result-accordion">
                        <Accordion
                            :items="[
                                { title: $t('sections.price_calculator.earnings_estimation_title'), status: 'info' },
                            ]"
                        >
                            <template #item-0>
                                <div class="price-calculator__calculation-info">
                                    {{ earningsEstimationDescription }}
                                </div>
                            </template>
                        </Accordion>
                    </div>

                    <div class="price-calculator__buttons price-calculator__result-buttons">
                        <LocalizedLink
                            v-if="isLoggedIn && isOwner"
                            :to="{ name: 'owner-publish-vehicle' }"
                            class="button button--primary button--full-width"
                        >
                            {{ $t('sections.price_calculator.list_your_camper') }}
                        </LocalizedLink>
                        <ModalLink
                            v-else
                            to="sign-up"
                            :data="{ role: 'owner' }"
                            class="button button--primary button--full-width"
                        >
                            {{ $t('sections.price_calculator.list_your_camper') }}
                        </ModalLink>
                        <button
                            class="button button--secondary-contrast button--full-width"
                            @click="displaySupportRequestModal"
                        >
                            {{ $t('sections.price_calculator.book_a_call') }}
                        </button>
                    </div>
                </template>

                <template v-else>
                    <h2 class="price-calculator__heading section__heading">
                        <template v-if="importedVehicleData">
                            {{ $t('sections.price_calculator.is_correct_heading', { name: formData.name }) }}
                        </template>
                        <template v-else>
                            {{ $t('sections.price_calculator.heading') }}
                        </template>
                    </h2>

                    <p v-if="!importedVehicleData && !inputVehicleDataManually" class="price-calculator__description">
                        <template v-if="vehicleDataImportAllowed">
                            {{ $t('sections.price_calculator.vehicle_data_import_description') }}
                        </template>
                        <template v-else>
                            {{ $t('sections.price_calculator.manual_description') }}
                        </template>
                    </p>

                    <div v-if="importedVehicleData" class="price-calculator__vehicle-info">
                        <i18n
                            class="price-calculator__vehicle-description"
                            tag="p"
                            path="sections.price_calculator.result_description"
                        >
                            <template v-if="!missingModelYearInImportedData" #modelYear>
                                {{ $t('sections.price_calculator.from') }}
                                <span>{{ modelYear }}</span>
                            </template>
                            <template #vehicleType>
                                <span>{{ vehicleTypeText(1, true) }}</span>
                            </template>
                            <template #location>
                                <span>{{ formData.location.name }}</span>
                            </template>
                        </i18n>
                        <i18n
                            class="price-calculator__vehicle-description"
                            tag="p"
                            path="sections.price_calculator.email_info"
                        >
                            <template #email>
                                <span>{{ formData.email }}</span>
                            </template>
                        </i18n>

                        <div class="price-calculator__imported-data">
                            <p class="price-calculator__imported-data__title">
                                {{ $t('sections.price_calculator.about_vehicle') }}
                            </p>
                            <Notice type="warning">
                                <ul class="vehicle-data">
                                    <li class="vehicle-data__item">
                                        <label class="vehicle-data__item-label">{{
                                            $t('vehicle_fields.license_plate_label')
                                        }}</label>
                                        <span class="vehicle-data__item-value">
                                            {{ importedVehicleData.licensePlate }}
                                        </span>
                                    </li>
                                    <li class="vehicle-data__item">
                                        <label class="vehicle-data__item-label">{{
                                            $t('vehicle_fields.brand_label')
                                        }}</label>
                                        <span class="vehicle-data__item-value">
                                            {{ importedVehicleData.brand.name }}
                                        </span>
                                    </li>
                                    <li class="vehicle-data__item">
                                        <label class="vehicle-data__item-label">{{
                                            $t('vehicle_fields.model_label')
                                        }}</label>
                                        <span class="vehicle-data__item-value">
                                            {{ importedVehicleData.model }}
                                        </span>
                                    </li>
                                    <li v-if="importedVehicleData.modelYear" class="vehicle-data__item">
                                        <label class="vehicle-data__item-label">{{
                                            $t('vehicle_fields.model_year_full_label')
                                        }}</label>
                                        <span class="vehicle-data__item-value">
                                            {{ importedVehicleData.modelYear }}
                                        </span>
                                    </li>
                                </ul>
                            </Notice>
                        </div>
                    </div>

                    <div v-if="inputVehicleDataManually" class="price-calculator__use-reg-number">
                        <a @click="inputVehicleDataManually = false">
                            {{ $t('sections.price_calculator.use_registration_number') }}
                        </a>
                    </div>

                    <form class="form" @submit.prevent="handleFormSubmit">
                        <div v-if="useAutoDataImport" class="form__row">
                            <label for="license-plate" class="label">{{
                                $t('vehicle_fields.license_plate_label')
                            }}</label>
                            <LicensePlateInput
                                id="license-plate"
                                v-model="$v.formData.licensePlate.$model"
                                :class="{
                                    'input--error':
                                        error === 'vehicle_not_found' ||
                                        ($v.formData.licensePlate.$invalid && $v.formData.licensePlate.$dirty),
                                }"
                            />
                            <div class="price-calculator__link-wrapper">
                                <a @click="inputVehicleDataManually = true">
                                    {{ $t('sections.price_calculator.input_info_manually') }}
                                </a>
                            </div>
                        </div>

                        <div v-if="!vehicleDataImportAllowed && !importedVehicleData" class="form__row">
                            <label class="label">{{ $t('sections.price_calculator.vehicle_location') }}</label>
                            <Location
                                ref="googleLocation"
                                v-model="$v.formData.location.$model"
                                :always-popup="true"
                                :placeholder="$t('form_inputs.city_or_region')"
                                :show-geolocation-button="false"
                                :has-error="$v.formData.location.$invalid && $v.formData.location.$dirty"
                            />
                        </div>

                        <div
                            v-if="isModelYearVisible && inputVehicleDataManually && !importedVehicleData"
                            class="form__row"
                        >
                            <label class="label">
                                {{ $t('vehicle_fields.model_year_full_label') }}
                            </label>
                            <NumberInput
                                v-model="$v.formData.modelYear.$model"
                                :has-error="
                                    ($v.formData.modelYear.$dirty && $v.formData.modelYear.$invalid) ||
                                    ($v.formData.modelYear.$model &&
                                        ($v.formData.modelYear.$model.length === 0 ||
                                            $v.formData.modelYear.$model.length > 4))
                                "
                            />
                            <InputError
                                v-if="
                                    $v.formData.modelYear.$dirty &&
                                    $v.formData.modelYear.$model &&
                                    $v.formData.modelYear.$model.length >= 4 &&
                                    !$v.formData.modelYear.minValue
                                "
                            >
                                {{ $t('vehicle_forms.basic_data_form.model_year_error_too_old') }}
                            </InputError>
                            <InputError v-if="!$v.formData.modelYear.maxValue && $v.formData.modelYear.$dirty">
                                {{ $t('vehicle_forms.basic_data_form.model_year_error_year_in_future') }}
                            </InputError>
                        </div>

                        <div v-if="!importedVehicleData" class="form__row">
                            <label class="label">
                                {{ $t('vehicle_fields.sub_type_label') }}
                            </label>
                            <VehicleTypeSelector
                                v-model="$v.formData.subType.$model"
                                mode="horizontal"
                                variant="primary"
                            />
                        </div>

                        <div v-if="isModelYearVisible && !inputVehicleDataManually" class="form__row">
                            <i18n
                                v-if="missingModelYearInImportedData"
                                tag="label"
                                class="label"
                                path="sections.price_calculator.model_year_label"
                            >
                                <template #vehicleType>
                                    <span>{{ vehicleTypeText() }}</span>
                                </template>
                            </i18n>
                            <label v-else class="label">
                                {{ $t('vehicle_fields.model_year_full_label') }}
                            </label>
                            <NumberInput
                                v-model="$v.formData.modelYear.$model"
                                :has-error="
                                    ($v.formData.modelYear.$dirty && $v.formData.modelYear.$invalid) ||
                                    ($v.formData.modelYear.$model &&
                                        ($v.formData.modelYear.$model.length === 0 ||
                                            $v.formData.modelYear.$model.length > 4))
                                "
                            />
                            <InputError
                                v-if="
                                    $v.formData.modelYear.$dirty &&
                                    $v.formData.modelYear.$model &&
                                    $v.formData.modelYear.$model.length >= 4 &&
                                    !$v.formData.modelYear.minValue
                                "
                            >
                                {{ $t('vehicle_forms.basic_data_form.model_year_error_too_old') }}
                            </InputError>
                            <InputError v-if="!$v.formData.modelYear.maxValue && $v.formData.modelYear.$dirty">
                                {{ $t('vehicle_forms.basic_data_form.model_year_error_year_in_future') }}
                            </InputError>
                        </div>

                        <div v-if="vehicleDataImportAllowed && !importedVehicleData" class="form__row">
                            <label class="label">{{ $t('sections.price_calculator.vehicle_location') }}</label>
                            <Location
                                ref="googleLocation"
                                v-model="$v.formData.location.$model"
                                :always-popup="true"
                                :placeholder="$t('form_inputs.city_or_region')"
                                :show-geolocation-button="false"
                                :has-error="$v.formData.location.$invalid && $v.formData.location.$dirty"
                            />
                        </div>

                        <div v-if="!importedVehicleData" class="form__row">
                            <label class="label">
                                {{ $t('sections.price_calculator.about_you') }}
                            </label>
                            <input
                                id="name"
                                v-model.trim="$v.formData.name.$model"
                                class="input price-calculator__name-input"
                                :placeholder="$t('form_inputs.name_label')"
                                type="text"
                                :class="{
                                    'input--error': $v.formData.name.$invalid && $v.formData.name.$dirty,
                                }"
                            />
                            <input
                                id="email"
                                v-model.trim="$v.formData.email.$model"
                                class="input"
                                :placeholder="$t('common.email')"
                                type="email"
                                :class="{
                                    'input--error': $v.formData.email.$invalid && $v.formData.email.$dirty,
                                }"
                            />
                        </div>

                        <div>
                            <template v-if="error">
                                <Notice
                                    v-if="error === 'no_results'"
                                    type="warning"
                                    class="price-calculator__validation-error"
                                >
                                    <template #heading>
                                        {{ $t('sections.price_calculator.no_results_title') }}
                                    </template>
                                    <template #default>
                                        {{ $t('sections.price_calculator.no_results_description') }}
                                    </template>
                                </Notice>
                                <Notice v-else type="warning" class="price-calculator__validation-error">
                                    <template v-if="error === 'validation'">
                                        {{ $t('sections.price_calculator.error_validation') }}
                                    </template>
                                    <template v-else-if="error === 'vehicle_not_found'">
                                        {{ $t('sections.price_calculator.error_vehicle_not_found') }}
                                    </template>
                                    <template v-else>
                                        {{ $t('errors.an_error_occurred') }}
                                        {{ $t('errors.try_again_or_contact_us') }}
                                    </template>
                                </Notice>
                            </template>

                            <div class="price-calculator__buttons">
                                <button
                                    :disabled="$v.formData.$invalid || isLoading"
                                    class="button button--primary button--full-width"
                                    type="submit"
                                >
                                    <LoadingIndicator v-if="isLoading" />
                                    <template v-else>{{ buttonText }}</template>
                                </button>
                                <button
                                    v-if="importedVehicleData"
                                    class="button button--secondary-contrast button--full-width"
                                    @click="importedVehicleData = null"
                                >
                                    {{ $t('sections.price_calculator.go_back') }}
                                </button>
                            </div>
                        </div>
                    </form>
                </template>
            </div>
        </div>
    </section>
</template>

<script>
import required from 'vuelidate/lib/validators/required';
import email from 'vuelidate/lib/validators/email';
import { validationMixin } from 'vuelidate';

import { maxValue, minValue, requiredIf } from 'vuelidate/lib/validators';
import { mapGetters } from 'vuex';
import { actionTypes as leadActions } from '~/store/lead';
import modalMixin from '~/mixins/modalMixin';
import LicensePlateInput from '~/components/form-elements/LicensePlateInput.vue';
import Notice from '~/components/Notice.vue';
import LoadingIndicator from '~/components/LoadingIndicator.vue';
import VehicleTypeSelector from '~/components/form-elements/VehicleTypeSelector';
import Location from '~/components/form-elements/Location';
import NumberInput from '~/components/form-elements/number-input/NumberInput';
import InputError from '~/components/form-elements/InputError';
import { ASSET_SUB_TYPE_CAMPERVAN, ASSET_SUB_TYPE_MOTORHOME, ASSET_SUB_TYPE_OTHER_CAMPER } from '~/config/assetTypes';
import Accordion from '~/components/Accordion';
import ModalLink from '~/components/modals/ModalLink';

const MAX_YEAR = new Date().getFullYear() + 1;

export default {
    components: {
        ModalLink,
        Accordion,
        InputError,
        NumberInput,
        Location,
        VehicleTypeSelector,
        LicensePlateInput,
        Notice,
        LoadingIndicator,
    },
    mixins: [validationMixin, modalMixin],
    validations() {
        return {
            formData: {
                name: { required },
                email: {
                    required,
                    email,
                },
                licensePlate: {
                    required: requiredIf(() => {
                        return this.useAutoDataImport;
                    }),
                },
                subType: { required },
                location: { required },
                modelYear: {
                    required: requiredIf(() => this.isModelYearVisible),
                    minValue(value) {
                        if (!this.isModelYearVisible) {
                            return true;
                        }

                        return minValue(1950)(value);
                    },
                    maxValue(value) {
                        if (!this.isModelYearVisible) {
                            return true;
                        }

                        return maxValue(MAX_YEAR)(value);
                    },
                },
            },
        };
    },
    data() {
        const formData = {
            name: null,
            email: null,
            licensePlate: null,
            subType: ASSET_SUB_TYPE_MOTORHOME,
            location: null,
            modelYear: null,
        };

        const { isLoggedIn, user } = this.$store.getters;

        if (isLoggedIn) {
            formData.name = user.firstName;
            formData.email = user.email;
        }

        return {
            formData,
            error: null,
            importedVehicleData: null,
            isLoading: false,
            inputVehicleDataManually: false,
            leadPriceCalculations: null,
        };
    },
    computed: {
        ...mapGetters(['isLoggedIn', 'isOwner']),
        vehicleDataImportAllowed() {
            return this.$config.mcTld === 'com' && !['at', 'de'].includes(this.$i18n.locale);
        },
        useAutoDataImport() {
            return this.vehicleDataImportAllowed && !this.inputVehicleDataManually && this.importedVehicleData === null;
        },
        buttonText() {
            if (this.useAutoDataImport) {
                return this.$t('buttons.continue');
            }

            return this.$t('sections.price_calculator.get_earnings_button');
        },
        modelYear() {
            return this.importedVehicleData?.modelYear || this.formData.modelYear;
        },
        isModelYearVisible() {
            return (
                !this.vehicleDataImportAllowed || this.inputVehicleDataManually || this.missingModelYearInImportedData
            );
        },
        resultNoticeDescription() {
            return this.$t('sections.price_calculator.result_notice_desc1', {
                topOwnerRentalWeeks: this.$tc('common.weeks_count', Number(this.topOwnerRentalWeeks), {
                    count: Number(this.topOwnerRentalWeeks),
                }),
                earnings: this.formattedPrice(this.topOwnerEarnings, this.leadPriceCalculations?.currency, 0),
            });
        },
        topOwnerEarnings() {
            if (!this.leadPriceCalculations) {
                return 0;
            }

            return (
                this.leadPriceCalculations?.topOwnersPriceHighMedian * this.leadPriceCalculations?.topOwnersRentalNights
            );
        },
        topOwnerRentalWeeks() {
            if (!this.leadPriceCalculations) {
                return 0;
            }

            const weeks = this.leadPriceCalculations?.topOwnersRentalNights / 7;

            return Math.round(weeks);
        },
        earningsEstimationDescription() {
            return this.$t('sections.price_calculator.earnings_estimation_description', {
                vehicleTypeCount: this.vehicleTypeText(this.leadPriceCalculations?.sampleSize),
                yearDiff: this.$tc('common.year_count', this.leadPriceCalculations?.sampleModelYearDiff, {
                    count: this.leadPriceCalculations?.sampleModelYearDiff,
                }),
                totalVehicles: this.leadPriceCalculations?.sampleCountryTotalVehicles,
                country: this.getCountryInfo(),
            });
        },
        missingModelYearInImportedData() {
            return this.importedVehicleData && !this.importedVehicleData.modelYear;
        },
    },
    methods: {
        getCountryInfo(locale) {
            const country = this.formData.location?.country;

            if (['AT', 'DE'].includes(country)) {
                return this.$t(`sections.price_calculator.${country}_info`, locale);
            }

            return this.$t(`localization.countries.${country}`, locale);
        },
        vehicleTypeText(count = 1, capitalize = false) {
            let key = 'common.caravan';

            if (this.formData.subType === ASSET_SUB_TYPE_CAMPERVAN) {
                key = 'common.campervan';
            } else if (this.formData.subType === ASSET_SUB_TYPE_MOTORHOME) {
                key = 'common.motorhome';
            } else if (this.formData.subType === ASSET_SUB_TYPE_OTHER_CAMPER) {
                key = 'common.other_camper';
            }

            const vehicleType = this.$tc(key, count);
            const formattedType = count > 1 ? `${count} ${vehicleType}` : vehicleType;

            return capitalize ? formattedType.charAt(0).toUpperCase() + formattedType.slice(1) : formattedType;
        },
        getPriceCalculationData() {
            return {
                country: this.formData.location?.country,
                location: this.formData.location?.name,
                latitude: this.formData.location?.lat,
                longitude: this.formData.location?.lng,
                subType: this.formData.subType,
                modelYear: this.modelYear,
                name: this.formData.name,
                email: this.formData.email,
                licensePlate:
                    this.vehicleDataImportAllowed && !this.inputVehicleDataManually
                        ? this.formData.licensePlate?.text
                        : null,
            };
        },
        getLeadData() {
            const vehicle = this.importedVehicleData
                ? { ...this.importedVehicleData, subType: this.formData.subType }
                : {
                      modelYear: this.formData.modelYear,
                      subType: this.formData.subType,
                  };

            return {
                source: 'Price calculator',
                userType: 'Owner',
                firstName: this.formData.name,
                email: this.formData.email,
                country: this.formData.location?.country,
                preferredLocale: this.$i18n.localeProperties.iso,
                vehicle,
                priceCalculator: {
                    modelYear: this.modelYear,
                    location: this.formData.location?.name,
                    sampleSize: this.leadPriceCalculations?.sampleSize,
                    sampleCountryTotalVehicles: this.leadPriceCalculations?.sampleCountryTotalVehicles,
                    marketCopy: this.getCountryInfo('en'),
                    topOwnerEarnings: Math.round(this.$root.$options.filters.price(this.topOwnerEarnings)),
                    topOwnerRentalWeeks: this.topOwnerRentalWeeks,
                    currency: this.leadPriceCalculations?.currency,
                },
            };
        },
        handleError(error, message) {
            this.$sentry.withScope(scope => {
                scope.setExtra('error', error);
                this.$sentry.captureMessage(message, 'error');
            });

            this.error = error.slug || 'unknown_error';
        },
        validateForm() {
            this.error = null;
            this.$v.$touch();

            if (this.$v.$invalid) {
                this.error = 'validation';

                return false;
            }

            return true;
        },
        handleFormSubmit() {
            if (!this.validateForm()) {
                return;
            }

            if (this.useAutoDataImport) {
                this.getVehicleData();
            } else {
                this.submitLeadPriceCalculation();
            }
        },
        scrollToTopOfSection() {
            this.$refs.priceCalculator.scrollIntoView({ behavior: 'smooth', block: 'start' });
        },
        async getVehicleData() {
            const { country, text: licensePlate } = this.formData.licensePlate;

            this.isLoading = true;

            try {
                this.importedVehicleData = await this.$vehicleService.getVehicleData(country, licensePlate);
                this.$v.$reset();
                this.error = null;
                this.scrollToTopOfSection();
            } catch (error) {
                if (error.slug === 'model_not_found') {
                    this.error = 'vehicle_not_found';

                    return;
                }

                this.handleError(error, `Price calculator failure. Error: ${error.message}`);
            } finally {
                this.isLoading = false;
            }
        },
        async submitLeadPriceCalculation() {
            this.isLoading = true;

            try {
                const data = this.getPriceCalculationData();
                this.leadPriceCalculations = await this.$priceCalculationsService.calculateLeadPrice(data);

                if (this.leadPriceCalculations == null) {
                    this.error = 'no_results';
                } else {
                    this.scrollToTopOfSection();
                    await this.createLead();
                }
            } catch (error) {
                this.handleError(error, `Lead price calculation failure. Error: ${error.message}`);
            } finally {
                this.isLoading = false;
            }
        },
        async createLead() {
            this.isLoading = true;
            const data = this.getLeadData();

            try {
                await this.$store.dispatch(leadActions.STORE_LEAD, data);
                this.dispatchTrackingEvent();
            } catch (error) {
                this.handleError(error, `Failed to create lead. Error: ${error.message}`);
            } finally {
                this.isLoading = false;
            }
        },
        dispatchTrackingEvent() {
            if (this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event: 'lead_pricecalculator',
                    method: this.formData.location?.country,
                    page_type: 'rentoutpage',
                    locale: this.$i18n.locale,
                });
            }
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

.price-calculator {
    padding: 32px 0;
    text-align: left;
    @include font-size(16px);
    position: relative;
    overflow: hidden;

    @include md {
        padding: 50px 0;
    }

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: $cream-white;
        z-index: 0;

        @include lg {
            width: 33%;
        }
    }

    &__bg-image {
        position: absolute;
        top: 0;
        right: 0;
        width: 67%;
        height: 100%;
        background-size: cover;
        z-index: 0;
        display: none;

        @include lg {
            display: block;
        }
    }

    &__card {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        min-height: 740px;
        min-width: 100%;
        position: relative;
        @extend %card;
        background-color: $almost-white;
        border-radius: 16px;
        padding: 32px 16px;
        z-index: 1;
        box-sizing: border-box;

        @include md {
            padding: 56px 40px;
            min-height: 780px;
        }

        @include lg {
            min-width: 720px;
            max-width: 720px;
        }

        @media screen and (min-width: 1400px) {
            min-width: 50%;
        }
    }

    .label {
        @include font-size(16px);
    }

    .vehicle-type-selector {
        &__item {
            flex: 1 0 0;

            @include lg {
                padding: 0 4px;
            }
        }
    }

    .vehicle-type__button {
        padding: 4px 10px 4px 0;
    }

    &__description {
        margin-bottom: 24px;
    }

    &__name-input {
        margin-bottom: 4px;
    }

    &__link-wrapper {
        text-align: right;

        a {
            @include font-size(14px);
        }
    }

    .vehicle-data {
        &__item {
            margin-bottom: 8px;

            &:last-child {
                margin-bottom: 0;
            }
        }

        &__item-label {
            color: $primary;
        }

        &__item-value {
            font-weight: bold;
        }
    }

    &__heading {
        @extend %heading-1;
        margin-bottom: 24px;

        @include lg {
            margin-bottom: 32px;
            line-height: 56px;
        }
    }

    &__vehicle-info {
        margin-bottom: 24px;
    }

    &__imported-data {
        &__title {
            margin-bottom: 8px;
            font-weight: bold;

            @include lg {
                @include font-size(20px);
                margin-bottom: 16px;
            }
        }
    }

    &__validation-error {
        margin-bottom: 16px;
    }

    &__use-reg-number {
        margin-bottom: 40px;
    }

    .form {
        display: flex;
        flex-direction: column;
    }

    &__vehicle-description {
        margin-bottom: 24px;
        span {
            font-weight: bold;
        }

        @include lg {
            margin-bottom: 32px;
        }
    }

    &__result-buttons {
        margin-top: 170px;

        @include lg {
            margin-top: 130px;
        }
    }

    &__result-heading {
        @include font-size(24px);
        line-height: 40px;
        font-weight: 600;
        margin-bottom: 24px;

        @include lg {
            @include font-size(30px);
            line-height: 52px;
        }
    }

    &__result-price {
        @include font-size(36px);
        color: $secondary-dark;

        @include lg {
            @include font-size(52px);
        }
    }

    #control-earnings .notice {
        margin: 24px 0;
        background-color: $tertiary-light;
        border: none;
        padding: 16px;

        .notice__heading {
            margin-bottom: 16px;

            @include lg {
                @include font-size(20px);
            }
        }

        @include lg {
            margin: 32px 0;
            padding: 24px;
        }
    }

    &__result-accordion .accordion {
        padding: 0;

        &__header {
            padding-bottom: 8px;
            justify-content: flex-start;
        }

        &__title-text {
            @include font-size(16px);
        }

        &__content {
            padding: 0;
            position: absolute;
        }

        .svg-icon {
            width: 24px;
            height: 24px;
        }
    }

    &__calculation-info {
        @include font-size(14px);
        line-height: 20px;
    }

    &__buttons {
        .button--primary {
            min-height: 45px;
        }

        .button--secondary-contrast {
            margin-top: 16px;
        }
    }

    .location-input__predictions {
        width: 100%;
    }
}
</style>
